import React, { KeyboardEvent, ReactElement, forwardRef, useContext, useEffect, useRef } from 'react';

import { useId, useMessageFormatter } from '@redsift/design-system';
import intlMessages from '../combobox/intl';

import {
  Comp,
  EventKey,
  FocusWithinGroupActionType,
  FocusWithinGroupContext,
  IconButton,
  ListboxActionType,
  ListboxContext,
  isComponent,
  useTheme,
} from '@redsift/design-system';
import { ComboboxTriggerProps } from './types';
import { useMergeRefs, usePopoverContext } from '@redsift/popovers';
import { ComboboxContext } from '../combobox/context';
import { mdiChevronDown, mdiClose } from '@redsift/icons';
import { ComboboxSelectionMode, ComboboxVariant } from '../combobox/types';
import classNames from 'classnames';

const COMPONENT_NAME = 'ComboboxTrigger';
const CLASSNAME = 'redsift-combobox-trigger';

/**
 * The ComboboxTrigger component.
 */
export const ComboboxTrigger: Comp<ComboboxTriggerProps, HTMLButtonElement> = forwardRef((props, ref) => {
  const [id] = useId();
  const { children, hideExpandButton, openOnFocus, hideClearButton, pillProps, renderPillContent, ...otherProps } =
    props;

  const format = useMessageFormatter(intlMessages);

  const { getReferenceProps, isOpen, handleOpen: handleOpenPopover, refs } = usePopoverContext();
  const childrenRef = (children as any).ref;
  const triggerRef = useMergeRefs([refs.setReference, ref, childrenRef]);

  const focusContext = useContext(FocusWithinGroupContext);
  const comboboxState = useContext(ComboboxContext);
  const listboxState = useContext(ListboxContext);
  const theme = useTheme();

  const canHaveMultipleSelections = comboboxState?.selectionMode === ComboboxSelectionMode.multiple;
  const canHaveFreeText = comboboxState?.variant === ComboboxVariant.suggestions;

  const renderedChildren =
    typeof children === 'function' ? children({ value: comboboxState?.value, isOpen }) : children;

  const isTextArea = isComponent('TextArea')(renderedChildren);
  const isTextField = isComponent('TextField')(renderedChildren);
  const hasMultipleLines = isTextArea && comboboxState?.inputValue.includes('\n');

  const _inputRef = useRef<HTMLInputElement>();
  const inputRef = renderedChildren.props.inputRef ?? _inputRef;

  const handleChange = (value?: string) => {
    focusContext.dispatch({
      type: FocusWithinGroupActionType.FILTER_LIST,
      payload: { filter: { value: value || '', ...comboboxState?.filter! } },
    });

    comboboxState?.setInputValue(value!);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const code = event.code;

    if (code === 'Escape' || code === 'Tab') {
      if (isOpen) {
        handleOpenPopover(false);
      }

      if (canHaveMultipleSelections && canHaveFreeText && comboboxState.inputValue) {
        listboxState.dispatch({
          type: ListboxActionType.TOGGLE,
          payload: {
            value: comboboxState.inputValue,
            activedescendant: [comboboxState.freeTextItemId as string, comboboxState.inputValue],
          },
        });
      }
    }

    if ((/^.$/.test(event.key) || code === 'Backspace') && !hasMultipleLines) {
      if (!isOpen) {
        handleOpenPopover(true);
      }

      if (canHaveMultipleSelections && canHaveFreeText) {
        focusContext.dispatch({
          type: FocusWithinGroupActionType.DELAY_ACTION,
          payload: {
            type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
            payload: {
              key: EventKey.Home,
              ctrlKey: event.ctrlKey,
            },
          },
        });
      }
    }

    if (canHaveMultipleSelections && comboboxState.inputValue === '' && code === 'Backspace') {
      listboxState.dispatch({
        type: ListboxActionType.SET,
        payload: {
          values: (comboboxState.value as string[]).slice(0, (comboboxState.value as string[]).length - 1),
          activedescendant: focusContext.state.activedescendant,
        },
      });
    }

    if (code === 'ArrowDown' && !hasMultipleLines) {
      if (!isOpen) {
        handleOpenPopover(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: focusContext.state.selectedId !== null ? EventKey.ArrowDown : EventKey.Home,
            ctrlKey: event.ctrlKey,
          },
        },
      });
      event.preventDefault();
    }

    if (code === 'ArrowUp' && !hasMultipleLines) {
      if (!isOpen) {
        handleOpenPopover(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: focusContext.state.selectedId !== null ? EventKey.ArrowUp : EventKey.End,
            ctrlKey: event.ctrlKey,
          },
        },
      });
      event.preventDefault();
    }

    if (code === 'Enter') {
      if (isTextField || (isTextArea && !event.shiftKey)) {
        event.preventDefault();
      }

      if (
        canHaveMultipleSelections &&
        canHaveFreeText &&
        focusContext.state.activedescendant?.[0] === comboboxState.freeTextItemId &&
        comboboxState.inputValue
      ) {
        listboxState.dispatch({
          type: ListboxActionType.TOGGLE,
          payload: {
            value: comboboxState.inputValue,
            activedescendant: [comboboxState.freeTextItemId as string, comboboxState.inputValue],
          },
        });
        handleOpenPopover(false);
      } else if (isOpen && focusContext.state.selectedId) {
        if (canHaveMultipleSelections) {
          listboxState.dispatch({
            type: ListboxActionType.TOGGLE,
            payload: {
              value: focusContext.state.activedescendant?.[1] ?? '',
              activedescendant: focusContext.state.activedescendant,
            },
          });
        } else {
          listboxState.dispatch({
            type: ListboxActionType.SET,
            payload: {
              values: [focusContext.state.activedescendant?.[1] ?? ''],
              activedescendant: focusContext.state.activedescendant,
            },
          });
        }
      } else if (!event.shiftKey) {
        comboboxState?.formRef?.current?.submit();
        comboboxState?.submitRef?.current?.click();
      }
    }
  };

  const handleOpen = () => {
    if (isOpen) {
      handleOpenPopover(false);
    } else {
      handleOpenPopover(true);
      inputRef.current?.focus();
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.FOCUS_ON_LIST,
          payload: {
            id: focusContext.state.activedescendant?.[0]!,
            filter: { value: '', ...comboboxState?.filter! },
          },
        },
      });
    }
  };

  const handleFocus = () => {
    if (openOnFocus) {
      handleOpen();
    }
    inputRef.current?.select();
  };

  const handleBlur = () => {
    if (canHaveMultipleSelections) {
      if (!canHaveFreeText) {
        comboboxState?.setInputValue('');
      }
    } else {
      if (canHaveFreeText) {
        comboboxState?.setValue(comboboxState.inputValue);
      } else {
        comboboxState?.setInputValue(listboxState.state.selectedValues[0]);
      }
    }
  };

  const handleClear = () => {
    listboxState.dispatch({
      type: ListboxActionType.SET,
      payload: {
        values: [],
        activedescendant: ['', ''],
      },
    });
  };

  useEffect(() => {
    if (canHaveMultipleSelections) {
      focusContext.dispatch({
        type: FocusWithinGroupActionType.FILTER_LIST,
        payload: { filter: { value: '', ...comboboxState?.filter! } },
      });
      comboboxState?.setValue(listboxState.state.selectedValues);
      comboboxState?.setInputValue('');
      handleOpenPopover(false);
    } else {
      focusContext.dispatch({
        type: FocusWithinGroupActionType.FILTER_LIST,
        payload: { filter: { value: listboxState.state.selectedValues[0] || '', ...comboboxState?.filter! } },
      });
      comboboxState?.setValue(listboxState.state.selectedValues[0]);
      if (listboxState.state.selectedValues.length) {
        comboboxState?.setInputValue(listboxState.state.selectedValues[0]);
      }
      handleOpenPopover(false);
    }
  }, [listboxState.state]);

  if (isTextField || isTextArea) {
    const {
      'aria-controls': ariaControls,
      'aria-expanded': ariaExpanded,
      'aria-haspopup': ariaHaspopup,
      role,
      ...forwardedProps
    } = getReferenceProps({
      ref: triggerRef,
      ...otherProps,
      ...(renderedChildren as ReactElement).props,
      children: (renderedChildren as ReactElement).props.children ?? '',
    });

    const inputProps = {
      'aria-activedescendant': isOpen ? focusContext.state.activedescendant?.[0] : undefined,
      'aria-controls': ariaControls,
      'aria-expanded': ariaExpanded,
      'aria-haspopup': ariaHaspopup,
      autoComplete: 'off',
      autoCapitalize: 'off',
      autoCorrect: 'off',
      spellCheck: 'false',
      'aria-autocomplete': canHaveMultipleSelections ? 'list' : undefined,
      'aria-description': canHaveMultipleSelections
        ? format('selected', { count: comboboxState?.value.length })
        : undefined,
      onKeyDown: handleKeyDown,
      role,
    };

    return React.cloneElement(renderedChildren, {
      ...forwardedProps,
      theme,
      className: classNames((renderedChildren as ReactElement).props.className, comboboxState?.triggerClassName),
      isDisabled: comboboxState?.isDisabled,
      isInvalid: comboboxState?.isInvalid,
      onChange: handleChange,
      onBlur: handleBlur,
      onFocus: handleFocus,
      value: comboboxState?.inputValue,
      ...(isTextField
        ? {
            inputProps,
            inputRef,
            onClear: handleClear,
            hasClearButton: hideClearButton
              ? false
              : {
                  isDisabled: comboboxState?.isDisabled || (comboboxState?.value && comboboxState?.value?.length === 0),
                  onClick: () => inputRef.current.focus(),
                },
            pills: canHaveMultipleSelections
              ? (comboboxState?.value as string[]).map((v) => ({
                  isDisabled: comboboxState?.isDisabled,
                  children: [
                    renderPillContent ? renderPillContent(v) : v,
                    <IconButton
                      key={`${id}-pill-${v}-icon-button`}
                      icon={mdiClose}
                      aria-label={format('unselect', { label: v })}
                      onClick={(event) => {
                        event.preventDefault();
                        listboxState.dispatch({
                          type: ListboxActionType.TOGGLE,
                          payload: {
                            value: v,
                            activedescendant: focusContext.state.activedescendant,
                          },
                        });
                      }}
                    />,
                  ],
                  ...pillProps,
                }))
              : undefined,
            internal: (value?: string, isDisabled?: boolean, isInvalid?: boolean, isRequired?: boolean) => {
              return (
                <>
                  {typeof (renderedChildren as ReactElement).props.internal === 'function'
                    ? (renderedChildren as ReactElement).props.internal(value, isDisabled, isInvalid, isRequired)
                    : (renderedChildren as ReactElement).props.internal}
                  {hideExpandButton ? null : (
                    <IconButton
                      theme={theme}
                      aria-label={format(!isOpen ? 'expand' : 'collapse')}
                      color="grey"
                      icon={mdiChevronDown}
                      onClick={handleOpen}
                      isDisabled={isDisabled}
                      tabIndex={-1}
                      iconProps={{
                        style: {
                          transition: 'transform 300ms ease-out',
                          transform: `rotate(${isOpen ? '-180deg' : '0'})`,
                        },
                      }}
                    />
                  )}
                </>
              );
            },
          }
        : {
            textareaProps: inputProps,
            textareaRef: inputRef,
          }),
    });
  }

  return <>{renderedChildren}</>;
});
ComboboxTrigger.className = CLASSNAME;
ComboboxTrigger.displayName = COMPONENT_NAME;
